import React from "react";
import ErrorBoundaryFallback from "./ErrorBoundaryFallback";
import { importLangResources } from "locale";
import { captureException, withScope } from "@sentry/browser";
import storage from "@storage";

importLangResources("boundary");

const CHUNK_ERROR_RELOAD_KEY = "chunk_error_reload";

const detectChunkError = error =>
  error?.name === "ChunkLoadError" ||
  error?.message?.includes("Loading chunk") ||
  error?.message?.includes("Loading CSS chunk") ||
  error?.message?.includes("Failed to fetch dynamically imported module") ||
  error?.message?.includes("error loading dynamically imported module") ||
  error?.message?.includes("Importing a module script failed");

class ErrorBoundary extends React.Component {
  state = {
    error: "",
    eventId: "",
    errorInfo: "",
    hasError: false,
    isChunkError: false
  };

  static getDerivedStateFromError(error) {
    return { hasError: true, error, isChunkError: detectChunkError(error) };
  }

  componentDidCatch(error, errorInfo) {
    const isChunkError = detectChunkError(error);

    if (isChunkError) {
      const hasRefreshed = storage.session.get(CHUNK_ERROR_RELOAD_KEY);

      if (!hasRefreshed) {
        storage.session.set(CHUNK_ERROR_RELOAD_KEY, "true");
        window.location.reload();
        return;
      }
    }
    withScope(scope => {
      scope.setExtras(errorInfo);
      const eventId = captureException(error);
      this.setState({ eventId, errorInfo });
    });
  }

  render() {
    if (this.state.hasError) {
      const hasRefreshed = storage.session.get(CHUNK_ERROR_RELOAD_KEY);
      if (this.state.isChunkError && !hasRefreshed) {
        return null;
      }

      return <ErrorBoundaryFallback eventId={this.state.eventId} />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
